interface IBudgetVehicleAndClientCardsProps {
  vehicleData?: {
    model?: string;
    year?: string;
    licensePlate?: string;
    chassis?: string;
    odometer?: string;
    hourmeter?: string;
    contract?: string;
    inWarranty?: boolean;
  };
  clientData?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  isMobile?: boolean;
  isLoading?: boolean;
}

const BudgetVehicleAndClientCardsSkeleton = ({
  isMobile,
}: {
  isMobile: boolean;
}) => (
  <div
    className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4 mb-6 ${isMobile ? "px-2" : "px-6"} items-stretch`}
    aria-busy="true"
    aria-label="Carregando dados do veículo e cliente"
  >
    <div className="flex-1 flex flex-col mr-3">
      <div className="font-semibold text-black text-size-13 bg-white p-1 rounded-t-lg">
        Veículo
      </div>
      <div className="bg-[#E5EBF0] rounded-b-lg p-4 relative flex-1">
        <div
          className={`flex ${isMobile ? "flex-col" : "flex-row"} items-start gap-4`}
        >
          <div
            className={`bg-white p-4 flex-shrink-0 flex items-center justify-center ${isMobile ? "w-[45px] h-[55px] mb-2" : "w-[55px] h-[70px] mr-2"}`}
          >
            <div
              className={`w-full h-full rounded bg-gray-200/90 animate-pulse ${isMobile ? "min-h-[27px]" : "min-h-[38px]"}`}
            />
          </div>
          <div className="flex-1 space-y-2 w-full min-w-0">
            <div className="h-[15px] w-full max-w-[260px] rounded-sm bg-gray-300/80 animate-pulse" />
            <div className="h-[15px] w-full max-w-[220px] rounded-sm bg-gray-300/80 animate-pulse" />
            <div className="h-[15px] w-full max-w-[180px] rounded-sm bg-gray-300/80 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
    <div className="flex-1 flex flex-col">
      <div className="font-semibold text-black text-size-13 bg-white p-1 rounded-t-lg">
        Cliente
      </div>
      <div className="bg-white border border-gray-300 rounded-b-lg p-4 flex-1 space-y-2">
        <div className="h-[15px] w-full max-w-[200px] rounded-sm bg-gray-200 animate-pulse" />
        <div className="h-[12px] w-full max-w-[120px] rounded-sm bg-gray-200 animate-pulse" />
        <div className="h-[12px] w-full max-w-[240px] rounded-sm bg-gray-200 animate-pulse" />
      </div>
    </div>
  </div>
);

const BudgetVehicleAndClientCards = (props: IBudgetVehicleAndClientCardsProps) => {
  const isMobile = props.isMobile || false;
  const vehicle = props.vehicleData;
  const client = props.clientData;

  if (props.isLoading) {
    return <BudgetVehicleAndClientCardsSkeleton isMobile={isMobile} />;
  }

  if (!vehicle && !client) {
    return null;
  }

  return (
    <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-4 mb-6 ${isMobile ? 'px-2' : 'px-6'} items-stretch`}>
      {vehicle && (
        <div className="flex-1 flex flex-col mr-3">
          <div className="font-semibold text-black text-size-13 bg-white p-1 rounded-t-lg">Veículo</div>
          <div className="bg-[#E5EBF0] rounded-b-lg p-4 relative flex-1">
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-start gap-4`}>
              <div className={`bg-white p-4 flex-shrink-0 ${isMobile ? 'w-[45px] h-[55px]' : 'w-[55px] h-[70px]'} flex items-center justify-center ${isMobile ? 'mb-2' : 'mr-2'}`}>
                <img
                  src="/assets/images/truck.svg"
                  alt="Veículo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1">
                {(vehicle.model || vehicle.year || vehicle.licensePlate) && (
                  <div className="font-300 text-[#344054] text-size-15 leading-tight">
                    {vehicle.model && <span className="font-semibold text-[#43516a] text-size-15">{vehicle.model}</span>}
                    {vehicle.year && <span className="font-300 text-[#43516a] text-size-15"> {vehicle.year}</span>}
                    {vehicle.licensePlate && <span className="font-300 text-[#344054] text-size-15"> | Placa: {vehicle.licensePlate}</span>}
                  </div>
                )}
                {vehicle.chassis && (
                  <div className="font-300 text-[#344054] text-size-15 leading-tight">Chassi - {vehicle.chassis}</div>
                )}
                {(vehicle.odometer || vehicle.hourmeter) && (
                  <div className="font-300 text-[#344054] text-size-15 leading-tight">
                    {vehicle.odometer && <span className="font-300 text-[#344054] text-size-15">Odômetro: {vehicle.odometer}</span>}
                    {vehicle.odometer && vehicle.hourmeter && ' | '}
                    {vehicle.hourmeter && <span className="font-300 text-[#344054] text-size-15">Horímetro: {vehicle.hourmeter}</span>}
                  </div>
                )}
                {vehicle.contract && (
                  <div className="text-size-14 leading-tight">
                    Volks total Prev: Contrato #{vehicle.contract}
                  </div>
                )}
              </div>
            </div>
            {vehicle.inWarranty === true && (
              <div className={`${isMobile ? 'relative top-0 right-0 mt-2' : 'absolute top-4 right-4 mt-3 mr-3'}`}>
                <span className="px-3 py-1 rounded-full text-size-12 bg-green-500 text-white font-bold">
                  Em garantia
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {client && (
        <div className="flex-1 flex flex-col">
          <div className="font-semibold text-black text-size-13 bg-white p-1 rounded-t-lg">Cliente</div>
          <div className="bg-white border border-gray-300 rounded-b-lg p-4 flex-1">
            {client.name && <div className="font-300 text-[#344054] text-size-14 mb-1">{client.name}</div>}
            {client.phone && <div className="font-300 text-[#344054] text-size-12 mb-1">{client.phone}</div>}
            {client.email && <div className={`font-300 text-[#344054] text-size-12 ${isMobile ? '' : 'text-right'}`}>{client.email}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetVehicleAndClientCards;
